import type UIAnalyticsEvent from '@atlaskit/analytics-next/UIAnalyticsEvent';
import { AtlBrowserStorageSession } from '@atlassian/browser-storage-controls';

const SESSION_APPS_KEY = 'forge-ui.seen-app-keys';

/**
 *  This module is used to track the seen app context keys in session storage specifically
 *  for the forge.ui.extension loaded analytic event
 *  - Stores the last accessed date and the seen app context keys in session storage
 *  - The last accessed date is used to determine if the session is expired
 *  - The seen app context keys are used to determine if the event has already been sent
 *  - Session storage is used to avoid sending duplicate events on page refreshes or navigations
 */

let storageState: { appKeys: Set<string> | null; lastUpdated: number | null } = {
	lastUpdated: null,
	appKeys: null,
};

// Use a number for faster comparisons
const getTodayAsNumberOfDaysSinceEpoch = (): number => {
	return Math.floor(Date.now() / 86400000);
};

const checkIsExpired = () => {
	const today = getTodayAsNumberOfDaysSinceEpoch();

	// If the last updated date is not today, the state has expired and so we reset it
	return today !== storageState.lastUpdated;
};

const resetState = () => {
	storageState.appKeys = new Set<string>();
	storageState.lastUpdated = getTodayAsNumberOfDaysSinceEpoch();
};

export const setupContextKeysFromSessionStorage = (): void => {
	// If the app context keys have values, this call has already been made by another renderer and so we can return here
	if (storageState.appKeys !== null) {
		return;
	}

	// Check for the app context keys in session storage and store it into memory
	// This is called once across any number of ForgeUIRenderers on the page guarded by the above `if` statement
	try {
		const appKeyfilterSessionStorageContextInStorage = JSON.parse(
			AtlBrowserStorageSession.getItem(SESSION_APPS_KEY) ?? '{}',
		);
		storageState = {
			appKeys: new Set(appKeyfilterSessionStorageContextInStorage.appKeys),
			lastUpdated: Number(appKeyfilterSessionStorageContextInStorage.lastUpdated),
		};
		// If the state is expired, we reset it
		if (checkIsExpired()) {
			resetState();
			return;
		}
	} catch {
		resetState();
	}
};

const writeToSessionStorage = () => {
	try {
		// Set a limit to avoid overloading session storage
		if (storageState?.appKeys?.size && storageState?.appKeys?.size < 100) {
			AtlBrowserStorageSession.setItem(
				SESSION_APPS_KEY,
				JSON.stringify({
					appKeys: Array.from(storageState.appKeys ?? []),
					lastUpdated: getTodayAsNumberOfDaysSinceEpoch(),
				}),
			);
		}
	} catch {
		// If session storage is not available, we don't want to throw an error
	}
};

// Catches navigation or page refresh events and writes to session storage synchronously
window.addEventListener('beforeunload', () => {
	writeToSessionStorage();
});

const shouldAllowAndTrackEvent = (contextKey: string): boolean => {
	// State has expired
	if (checkIsExpired()) {
		resetState();
		storageState.appKeys?.add(contextKey);
		return true;
	}

	if (storageState.appKeys?.has(contextKey)) {
		return false;
	}

	storageState.appKeys?.add(contextKey);
	return true;
};

const findAppContextKeyInEvent = (event: UIAnalyticsEvent): string | undefined => {
	if (!event.context) {
		return undefined;
	}

	for (const context of event.context) {
		const { appId, extensionKey } = context.forgeUIAttributes ?? {};
		if (appId && extensionKey) {
			return `${appId}/${extensionKey}`;
		}
	}
	return undefined;
};

export const extensionLoadedEventFilter = (event: UIAnalyticsEvent): boolean => {
	try {
		const appContextKey = findAppContextKeyInEvent(event);
		if (appContextKey) {
			const shouldAllow = shouldAllowAndTrackEvent(appContextKey);
			return shouldAllow;
		}
	} catch {
		// in case of any error, we let the event through, so we don't accidentally break analytics
	}
	return true;
};
